/**
 * Tebex Headless API Service
 * Handles all communication with Tebex API for FiveM store
 */

const TEBEX_BASE_URL = 'https://headless.tebex.io/api';
const TEBEX_IDENT = process.env.REACT_APP_TEBEX_IDENT;
const BACKEND_BASE_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.retrostore.dev').replace(/\/+$/, '');

/**
 * Get the complete and cancel URLs for checkout
 */
const getCheckoutUrls = () => {
  const baseUrl = window.location.origin;
  return {
    completeUrl: `${baseUrl}/checkout/success`,
    cancelUrl: `${baseUrl}/checkout/cancel`,
    authReturnUrl: `${baseUrl}/checkout/auth-return`
  };
};

/**
 * Create a new basket for checkout
 * @param {string} giftUsername - Optional username for gift recipient
 * @returns {Promise<Object>} Basket data including ident
 */
export const createBasket = async (giftUsername = null) => {
  // Validate Tebex ident is configured
  if (!TEBEX_IDENT || TEBEX_IDENT === 'undefined') {
    throw new Error('REACT_APP_TEBEX_IDENT is not configured. Please set it in your .env file.');
  }

  const { completeUrl, cancelUrl } = getCheckoutUrls();

  try {
    const basketData = {
      complete_url: completeUrl,
      cancel_url: cancelUrl,
      complete_auto_redirect: true
    };

    // If gifting to another user, include their username
    if (giftUsername) {
      basketData.username = giftUsername;
    }

    const response = await fetch(`${TEBEX_BASE_URL}/accounts/${TEBEX_IDENT}/baskets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(basketData)
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to create basket');
    }

    return data.data;
  } catch (error) {
    console.error('Tebex createBasket error:', error);
    throw error;
  }
};

/**
 * Check if authentication is required for the basket
 * @param {string} basketIdent - The basket identifier
 * @returns {Promise<{complete: boolean, links: Array}>} Auth status with complete flag and links array
 */
export const checkBasketAuth = async (basketIdent) => {
  const { authReturnUrl } = getCheckoutUrls();
  
  try {
    const response = await fetch(
      `${TEBEX_BASE_URL}/accounts/${TEBEX_IDENT}/baskets/${basketIdent}/auth?returnUrl=${encodeURIComponent(authReturnUrl)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      }
    );

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to check auth');
    }

    // API returns array directly at root level, not nested in data property
    // Empty array [] means user is already authenticated (no auth links needed)
    const links = Array.isArray(data) ? data : (data.data || []);
    
    return {
      complete: links.length === 0,  // Empty array = already authenticated
      links: links
    };
  } catch (error) {
    console.error('Tebex checkBasketAuth error:', error);
    throw error;
  }
};

/**
 * Add a package to the basket
 * @param {string} basketIdent - The basket identifier
 * @param {string|number} packageId - The Tebex package ID
 * @param {number} quantity - Quantity to add (default 1)
 * @returns {Promise<Object>} Updated basket data
 */
/**
 * Add package to basket
 * @param {string} basketIdent - The basket identifier
 * @param {string|number} packageId - The Tebex package ID
 * @param {number} quantity - Quantity to add (default 1)
 * @param {Object} options - Additional options
 * @param {string} options.targetUsername - Gift recipient's username (CFX.re username for FiveM)
 * @param {Object} options.variableData - Additional package variables
 * @param {Object} options.custom - Package-level custom data forwarded to Tebex webhooks
 * @returns {Promise<Object>} Updated basket data
 */
export const addPackageToBasket = async (basketIdent, packageId, quantity = 1, options = {}) => {
  const containsPrivateCustomData = Boolean(options.custom);

  try {
    // Build request body
    const body = {
      package_id: packageId,
      quantity: quantity
    };

    // IMPORTANT: Try target_username instead of target_username_id
    // Passing CFX.re username directly (Ident API has issues)
    if (options.targetUsername) {
      body.target_username = options.targetUsername;
      console.log('[TebexService] Adding GIFT package with target_username:', options.targetUsername);
    }

    // Add additional variable data if provided
    if (options.variableData) {
      body.variable_data = options.variableData;
    }

    // Package-level custom data is the signed-webhook correlation mechanism.
    // Never log it: Retro Football claim tokens are bearer credentials.
    if (containsPrivateCustomData) {
      body.custom = options.custom;
    }

    console.log('[TebexService] Adding package to basket:', {
      basketIdent,
      packageId,
      quantity,
      isGift: Boolean(options.targetUsername),
      hasCustomData: containsPrivateCustomData
    });
    
    const response = await fetch(`${TEBEX_BASE_URL}/baskets/${basketIdent}/packages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    });

    // Handle different response scenarios
    const text = await response.text();
    let data;
    
    try {
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      console.error('Failed to parse package response:', {
        status: response.status,
        packageId,
        hasCustomData: containsPrivateCustomData
      });
      if (!response.ok) {
        throw new Error(`Package API error (${response.status})`);
      }
      data = {};
    }
    
    if (!response.ok) {
      console.error('Tebex package API error:', {
        status: response.status,
        packageId,
        errorCode: data?.error_code || null,
        hasCustomData: containsPrivateCustomData
      });

      if (response.status === 403 || data.error_code === 'auth_required') {
        throw new Error('AUTH_REQUIRED');
      }
      if (response.status === 422) {
        throw new Error(
          containsPrivateCustomData
            ? 'Basket requires authentication'
            : (data.message || data.error || 'Basket requires authentication')
        );
      }

      // Handle 400 errors with better messaging
      if (response.status === 400) {
        const errorMsg = data.title || data.detail || data.message || 'Bad Request';
        if (!containsPrivateCustomData) {
          console.error(`Package add failed: ${errorMsg}`);
        }

        // If package requires variables, provide helpful error
        if (errorMsg.includes('requires variables')) {
          throw new Error('PACKAGE_REQUIRES_VARIABLES: This package requires additional information (like gift recipient). Please configure the package in Tebex to not require variables, or ensure variable_data is provided.');
        }

        throw new Error(
          containsPrivateCustomData
            ? 'Unable to add this package to the basket.'
            : errorMsg
        );
      }

      throw new Error(
        containsPrivateCustomData
          ? 'Failed to add package to basket'
          : (data.message || data.error || data.title || 'Failed to add package to basket')
      );
    }

    return data.data || data;
  } catch (error) {
    console.error('Tebex addPackageToBasket error:', {
      packageId,
      message: containsPrivateCustomData
        ? 'Package request failed with private custom data'
        : error?.message
    });
    throw error;
  }
};

/**
 * Remove a package from the basket
 * @param {string} basketIdent - The basket identifier
 * @param {string|number} packageId - The Tebex package ID
 * @returns {Promise<Object>} Updated basket data
 */
export const removePackageFromBasket = async (basketIdent, packageId) => {
  try {
    // Tebex API uses POST with body, not DELETE
    const response = await fetch(`${TEBEX_BASE_URL}/baskets/${basketIdent}/packages/remove`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        package_id: packageId
      })
    });

    // Read response body once
    const text = await response.text();
    
    // Handle empty response (204 No Content)
    if (response.status === 204 || !text) {
      return { success: true };
    }
    
    // Try to parse as JSON
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      if (!response.ok) {
        throw new Error(`API error: ${response.status} - ${text}`);
      }
      return { success: true };
    }
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to remove package from basket');
    }

    return data.data || data;
  } catch (error) {
    console.error('Tebex removePackageFromBasket error:', error);
    throw error;
  }
};

/**
 * Apply a coupon code to the basket
 * @param {string} basketIdent - The basket identifier
 * @param {string} couponCode - The coupon code
 * @returns {Promise<Object>} Updated basket data
 */
export const applyCoupon = async (basketIdent, couponCode) => {
  try {
    const response = await fetch(`${TEBEX_BASE_URL}/accounts/${TEBEX_IDENT}/baskets/${basketIdent}/coupons`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        coupon_code: couponCode
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Invalid coupon code');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Tebex applyCoupon error:', error);
    throw error;
  }
};

/**
 * Apply a creator code to the basket
 * @param {string} basketIdent - The basket identifier
 * @param {string} creatorCode - The creator code
 * @returns {Promise<Object>} Updated basket data
 */
export const applyCreatorCode = async (basketIdent, creatorCode) => {
  try {
    const response = await fetch(`${TEBEX_BASE_URL}/accounts/${TEBEX_IDENT}/baskets/${basketIdent}/creator-codes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        creator_code: creatorCode
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Invalid creator code');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Tebex applyCreatorCode error:', error);
    throw error;
  }
};

/**
 * Apply a gift card to the basket
 * @param {string} basketIdent - The basket identifier
 * @param {string} giftCardCode - The gift card code
 * @returns {Promise<Object>} Updated basket data
 */
export const applyGiftCard = async (basketIdent, giftCardCode) => {
  try {
    const response = await fetch(`${TEBEX_BASE_URL}/accounts/${TEBEX_IDENT}/baskets/${basketIdent}/giftcards`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        card_number: giftCardCode
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Invalid gift card');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Tebex applyGiftCard error:', error);
    throw error;
  }
};

/**
 * Get basket details
 * @param {string} basketIdent - The basket identifier
 * @returns {Promise<Object>} Basket data including checkout link
 */
export const getBasket = async (basketIdent) => {
  try {
    const response = await fetch(`${TEBEX_BASE_URL}/accounts/${TEBEX_IDENT}/baskets/${basketIdent}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get basket');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Tebex getBasket error:', error);
    throw error;
  }
};

/**
 * Get all packages through the cached Retro Store backend catalogue.
 * Checkout and basket mutations continue to call Headless Tebex directly.
 * @returns {Promise<Array>} Array of packages
 */
export const getPackages = async () => {
  try {
    const response = await fetch(`${BACKEND_BASE_URL}/api/tebex/packages`, {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to get packages' }));
      throw new Error(error.message || error.error || 'Failed to get packages');
    }

    const data = await response.json();
    const sourcePackages = Array.isArray(data)
      ? data
      : (Array.isArray(data.packages) ? data.packages : data.data);
    if (!Array.isArray(sourcePackages)) {
      throw new Error('Catalogue response did not contain a package array');
    }

    const packages = [...sourcePackages];
    const staleHeader = response.headers?.get?.('X-Retro-Catalog-Stale');
    const fetchedAt = response.headers?.get?.('X-Retro-Catalog-Fetched-At') || null;
    const catalogMeta = Object.freeze({
      stale: String(staleHeader).toLowerCase() === 'true',
      fresh: String(staleHeader).toLowerCase() !== 'true',
      fetchedAt,
      source: 'retrostore-backend'
    });
    Object.defineProperty(packages, 'catalogMeta', {
      value: catalogMeta,
      enumerable: false,
      configurable: false,
      writable: false
    });
    return packages;
  } catch (error) {
    console.error('Retro Store catalogue getPackages error:', error);
    throw error;
  }
};

/**
 * Get all categories from the Tebex store
 * @returns {Promise<Array>} Array of categories with packages
 */
export const getCategories = async () => {
  const packages = await getPackages();
  const categories = new Map();
  packages.forEach(pkg => {
    const id = pkg.categoryId || pkg.category?.id || pkg.categoryName || pkg.category?.name || 'uncategorized';
    const name = pkg.categoryName || pkg.category?.name || 'Uncategorized';
    if (!categories.has(id)) categories.set(id, { id, name, packages: [] });
    categories.get(id).packages.push(pkg);
  });
  return Array.from(categories.values());
};

/**
 * Get specific category with its packages
 * @param {number} categoryId - Category ID
 * @returns {Promise<Object>} Category with packages
 */
export const getCategoryPackages = async (categoryId) => {
  const categories = await getCategories();
  const category = categories.find(item => String(item.id) === String(categoryId));
  if (!category) throw new Error('Category not found');
  return category;
};

/**
 * Session storage helpers for basket persistence
 */
export const saveBasketToStorage = (basketIdent, pendingPackages = []) => {
  sessionStorage.setItem('tebex_basket_ident', basketIdent);
  if (pendingPackages.length > 0) {
    sessionStorage.setItem('tebex_pending_packages', JSON.stringify(pendingPackages));
  }
};

export const getBasketFromStorage = () => {
  return {
    basketIdent: sessionStorage.getItem('tebex_basket_ident'),
    pendingPackages: JSON.parse(sessionStorage.getItem('tebex_pending_packages') || '[]')
  };
};

export const clearBasketStorage = () => {
  sessionStorage.removeItem('tebex_basket_ident');
  sessionStorage.removeItem('tebex_pending_packages');
};

/**
 * Open CFX.re login popup for FiveM authentication
 * @param {string} authUrl - The authentication URL from Tebex
 * @returns {Window} The popup window reference
 */
export const openCfxLoginPopup = (authUrl) => {
  const width = 600;
  const height = 700;
  const left = (window.screen.width - width) / 2;
  const top = (window.screen.height - height) / 2;
  
  // Mark that we're opening a popup (for detection on return)
  sessionStorage.setItem('tebex_popup_opened', 'true');
  sessionStorage.setItem('tebex_popup_time', Date.now().toString());
  
  // Use a recognizable window name for popup detection
  const popup = window.open(
    authUrl,
    'tebex_auth_popup',
    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,status=yes`
  );
  
  return popup;
};

/**
 * Monitor popup for authentication completion
 * @param {Window} popup - The popup window reference
 * @param {Function} onComplete - Callback when auth completes
 * @param {Function} onClose - Callback when popup closes without auth
 */
export const monitorAuthPopup = (popup, onComplete, onClose) => {
  let authCompleted = false;
  let checkCount = 0;
  const maxChecks = 600; // 5 minutes max (500ms interval)
  
  const checkInterval = setInterval(() => {
    checkCount++;
    
    // Timeout after max checks
    if (checkCount > maxChecks) {
      clearInterval(checkInterval);
      if (!authCompleted) {
        onClose();
      }
      return;
    }
    
    try {
      // Check if popup was closed by user
      if (popup.closed) {
        clearInterval(checkInterval);
        if (!authCompleted) {
          onClose();
        }
        return;
      }
      
      // Try to access popup URL to check for success
      // This will only work when popup returns to our domain
      let popupUrl = '';
      try {
        popupUrl = popup.location.href;
      } catch (crossOriginError) {
        // Expected when popup is on external domain (CFX.re)
        // Continue monitoring without accessing URL
        return;
      }
      
      // Check if redirected back to our auth-return page or success indicator
      if (popupUrl && (
        popupUrl.includes('/checkout/auth-return') || 
        popupUrl.includes('success=true') ||
        popupUrl.includes('/authenticate/handle')
      )) {
        authCompleted = true;
        clearInterval(checkInterval);
        
        // Wait a moment before closing to ensure auth is processed
        setTimeout(() => {
          if (!popup.closed) {
            popup.close();
          }
          onComplete();
        }, 1000);
        return;
      }
      
      // Check for completion text in popup (when on same origin)
      try {
        if (popup.document && popup.document.body) {
          const bodyText = popup.document.body.innerText || '';
          if (bodyText.includes('Authentication Complete') || 
              bodyText.includes('Successfully authenticated') ||
              bodyText.includes('You can now close')) {
            authCompleted = true;
            clearInterval(checkInterval);
            
            setTimeout(() => {
              if (!popup.closed) {
                popup.close();
              }
              onComplete();
            }, 1000);
            return;
          }
        }
      } catch (docError) {
        // Cross-origin document access blocked, ignore
      }
    } catch (e) {
      // Cross-origin access blocked - popup is on external domain
      // This is expected during the CFX login process, keep monitoring
    }
  }, 500);
  
  // Return cleanup function
  return () => {
    clearInterval(checkInterval);
  };
};

/**
 * Wait for Tebex.js to be fully loaded
 * @param {number} timeout - Max time to wait in ms
 * @returns {Promise<boolean>}
 */
const waitForTebex = async (timeout = 5000) => {
  // If already available, resolve immediately
  if (typeof window.Tebex !== 'undefined' && window.Tebex.checkout) {
    return true;
  }

  // Load Tebex script on-demand to avoid impacting initial page load
  try {
    if (typeof window.__loadTebexScript === 'function') {
      await window.__loadTebexScript();
    }
  } catch (e) {
    console.error('Failed to load Tebex script:', e);
  }

  return new Promise((resolve) => {
    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (typeof window.Tebex !== 'undefined' && window.Tebex.checkout) {
        clearInterval(checkInterval);
        resolve(true);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        resolve(false);
      }
    }, 100);
  });
};

/**
 * Launch Tebex checkout as popup overlay using Tebex.js
 * Shows checkout directly on the page without redirecting
 * @param {string} basketIdent - The basket identifier (this IS the checkout ident)
 * @param {Object} callbacks - Optional callbacks for checkout events
 * @returns {Promise<void>}
 */
export const launchTebexCheckout = async (basketIdent, callbacks = {}) => {
  
  // IMPORTANT: Destroy any existing overlay first to prevent stacking
  // Use destroy() instead of close() to fully reset Tebex internal state
  try {
    if (typeof window.Tebex !== 'undefined' && window.Tebex.checkout) {
      window.Tebex.checkout.destroy();
      // Small delay to let it clean up properly
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  } catch (e) {
    // Ignore - Tebex may not be initialized
  }
  
  // Safety timeout - if nothing happens in 15 seconds, call onClose
  let launchSuccessful = false;
  const safetyTimeout = setTimeout(() => {
    if (!launchSuccessful) {
      if (callbacks.onClose) {
        callbacks.onClose();
      }
    }
  }, 15000);
  
  try {
    // Wait for Tebex.js to load (up to 10 seconds - increased timeout)
    const tebexAvailable = await waitForTebex(10000);
    
    if (!tebexAvailable) {
      clearTimeout(safetyTimeout);
      
      // Try to open checkout in new window
      try {
        const basket = await getBasket(basketIdent);
        if (basket?.links?.checkout) {
          const checkoutWindow = window.open(basket.links.checkout, '_blank', 'width=600,height=800');
          if (!checkoutWindow) {
            window.location.href = basket.links.checkout;
          }
        }
      } catch (e) {
        console.error('Failed to get basket for fallback:', e);
      }
      
      // Call onClose since we're not using the overlay
      if (callbacks.onClose) {
        callbacks.onClose();
      }
      return;
    }
    
    
    // Clear any existing event listeners to prevent duplicates
    try {
      window.Tebex.checkout.off('payment:complete');
      window.Tebex.checkout.off('close');
      window.Tebex.checkout.off('payment:error');
    } catch (e) {
      // Ignore - listeners may not exist
    }
    
    // Set up event listeners BEFORE init to catch any early events
    window.Tebex.checkout.on('payment:complete', (data) => {
      launchSuccessful = true;
      clearTimeout(safetyTimeout);
      if (callbacks.onPaymentComplete) {
        callbacks.onPaymentComplete(data);
      }
    });
    
    window.Tebex.checkout.on('close', () => {
      launchSuccessful = true;
      clearTimeout(safetyTimeout);
      if (callbacks.onClose) {
        callbacks.onClose();
      }
    });
    
    window.Tebex.checkout.on('payment:error', (error) => {
      launchSuccessful = true;
      clearTimeout(safetyTimeout);
      console.error('Tebex payment error:', error);
      if (callbacks.onError) {
        callbacks.onError(error);
      }
    });
    
    // Initialize Tebex.js checkout with the basket ident
    window.Tebex.checkout.init({
      ident: basketIdent,
      theme: 'dark'
    });
    
    // Launch the checkout popup overlay
    window.Tebex.checkout.launch();
    
    // Mark as successful after launch (overlay should be visible now)
    launchSuccessful = true;
    
  } catch (error) {
    console.error('Tebex checkout error:', error);
    clearTimeout(safetyTimeout);
    
    // Try fallback to new window
    try {
      const basket = await getBasket(basketIdent);
      if (basket?.links?.checkout) {
        const checkoutWindow = window.open(basket.links.checkout, '_blank', 'width=600,height=800');
        if (!checkoutWindow) {
          window.location.href = basket.links.checkout;
        }
      }
    } catch (e) {
      console.error('Fallback also failed:', e);
    }
    
    // Always call onError or onClose to reset state
    if (callbacks.onError) {
      callbacks.onError(error);
    } else if (callbacks.onClose) {
      callbacks.onClose();
    }
  }
};

/**
 * Close Tebex checkout overlay if open
 * Uses destroy() to fully reset state for subsequent opens
 */
export const closeTebexCheckout = () => {
  if (typeof window.Tebex !== 'undefined' && window.Tebex.checkout) {
    try {
      window.Tebex.checkout.destroy();
    } catch (e) {
      // Ignore - checkout may not be open
    }
  }
};

export default {
  createBasket,
  checkBasketAuth,
  addPackageToBasket,
  removePackageFromBasket,
  applyCoupon,
  applyCreatorCode,
  applyGiftCard,
  getBasket,
  getPackages,
  getCategories,
  getCategoryPackages,
  saveBasketToStorage,
  getBasketFromStorage,
  clearBasketStorage,
  openCfxLoginPopup,
  monitorAuthPopup,
  launchTebexCheckout,
  closeTebexCheckout
};
